// InsertIntoRouteButtons — reusable component for inserting a PlanPoint
// into the flight plan at any position (prepend, append, or insert
// between any two existing waypoints).
//
// Behavior:
//   - route empty (0 points): single primary "Add to flight plan" button
//   - route has 1 point: two buttons — "Add to start" / "Add to end"
//   - route has ≥2 points: primary "Add to end" + secondary "Insert…"
//     that opens a popover listing every gap:
//       "Before [WPT0]", "Between [WPT0] and [WPT1]", …, "After [last]"
//
// Styling follows the ember-600 / cockpit-800 pattern from NavaidDetail.

import { useRef, useState } from 'react';
import { Plus, MoveDown } from '../../shell/Icon';
import { usePlanStore, type PlanPoint } from '../../stores/planStore';

export interface InsertIntoRouteButtonsProps {
  point: PlanPoint;
  /** Called after any successful insert/add so the parent can close. */
  onDone?: () => void;
  className?: string;
  /**
   * Compact rendering for header chrome (e.g. AirportDetail) where the
   * buttons share space with info pills. Smaller text/padding, dropped
   * 'Insert…' label, content-width buttons (not full-flex).
   */
  compact?: boolean;
}

export default function InsertIntoRouteButtons({
  point,
  onDone,
  className = '',
  compact = false,
}: InsertIntoRouteButtonsProps) {
  const route = usePlanStore((s) => s.route);
  const add = usePlanStore((s) => s.add);
  const insertAt = usePlanStore((s) => s.insertAt);
  const [open, setOpen] = useState(false);
  const btnRef = useRef<HTMLButtonElement>(null);

  // Size primitives — toggled by `compact`.
  // In compact mode buttons sit at content-width (no flex-1) so they don't
  // dominate the airport-header row; the info pills get the leftover space.
  const txt   = compact ? 'text-[11px]' : 'text-sm';
  const pad   = compact ? 'px-2 py-0.5' : 'px-3 py-2';
  const rad   = compact ? 'rounded' : 'rounded-lg';
  const gap   = compact ? 'gap-1'  : 'gap-1.5';
  const icon  = compact ? 'h-3 w-3' : 'h-4 w-4';
  const grow  = compact ? ''       : 'flex-1';   // empty = content-width
  const primaryCls       = `${grow} inline-flex items-center justify-center ${gap} bg-ember-600 hover:bg-ember-500 text-white ${txt} ${rad} ${pad}`;
  const secondaryCls     = `${grow} inline-flex items-center justify-center ${gap} border border-cockpit-500 text-cockpit-200 hover:bg-cockpit-700 ${txt} ${rad} ${pad}`;
  const compactInsertCls = `inline-flex items-center justify-center ${gap} border border-cockpit-500 text-cockpit-200 hover:bg-cockpit-700 ${txt} ${rad} ${pad}`;

  function doAdd() {
    add(point);
    onDone?.();
  }

  function doInsertAt(idx: number) {
    insertAt(idx, point);
    setOpen(false);
    onDone?.();
  }

  // ── empty route ──────────────────────────────────────────────────
  if (route.length === 0) {
    return (
      <div className={className}>
        <button onClick={doAdd} className={compact ? `mt-1 ${primaryCls}` : `w-full mt-1 ${primaryCls}`}>
          <Plus className={icon} />
          {compact ? 'Add' : 'Add to flight plan'}
        </button>
      </div>
    );
  }

  // ── single waypoint in route ─────────────────────────────────────
  if (route.length === 1) {
    return (
      <div className={`flex gap-2 ${className}`}>
        <button onClick={() => doInsertAt(0)} className={secondaryCls}>
          {!compact && <MoveDown className={`${icon} rotate-180`} />}
          {compact ? '← Start' : 'Add to start'}
        </button>
        <button onClick={doAdd} className={primaryCls}>
          {!compact && <Plus className={icon} />}
          {compact ? 'End →' : 'Add to end'}
        </button>
      </div>
    );
  }

  // ── two or more waypoints ─────────────────────────────────────────
  const gaps: Array<{ label: string; idx: number }> = [];
  gaps.push({ label: `Before ${route[0].ident}`, idx: 0 });
  for (let i = 1; i < route.length; i++) {
    gaps.push({
      label: `Between ${route[i - 1].ident} and ${route[i].ident}`,
      idx: i,
    });
  }
  gaps.push({ label: `After ${route[route.length - 1].ident}`, idx: route.length });

  return (
    <div className={`flex gap-2 relative ${className}`}>
      {/* Primary: append */}
      <button onClick={doAdd} className={primaryCls}>
        {!compact && <Plus className={icon} />}
        {compact ? 'End →' : 'Add to end'}
      </button>

      {/* Secondary: insert at position */}
      <button
        ref={btnRef}
        onClick={() => setOpen((v) => !v)}
        className={compactInsertCls}
        aria-label="Insert at specific position in route"
        title="Insert at position…"
      >
        <MoveDown className={icon} />
        {!compact && <span className="hidden sm:inline text-xs">Insert…</span>}
      </button>

      {/* Popover */}
      {open && (
        <>
          {/* Backdrop — closes popover on outside click */}
          <div
            className="fixed inset-0 z-40"
            onClick={() => setOpen(false)}
            aria-hidden
          />
          {/* Menu */}
          <div className="absolute bottom-full right-0 mb-1 z-50 w-64 bg-cockpit-800 border border-cockpit-600 rounded-xl shadow-rail overflow-hidden">
            <div className="px-3 py-1.5 border-b border-cockpit-700 text-[10px] tracking-widest text-cockpit-300">
              INSERT INTO ROUTE
            </div>
            <ul className="divide-y divide-cockpit-700 max-h-56 overflow-y-auto">
              {gaps.map((g) => (
                <li key={g.idx}>
                  <button
                    className="w-full text-left px-3 py-2 text-sm text-cockpit-100 hover:bg-cockpit-700 active:bg-cockpit-600"
                    onClick={() => doInsertAt(g.idx)}
                  >
                    {g.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </>
      )}
    </div>
  );
}
